/**
 * User-friendly error handling for entitlement loading failures
 * Provides consistent error messages and recovery strategies
 */

import { createLogger } from './logger';

const entitlementLogger = createLogger('EntitlementError');

export interface EntitlementError {
  code: string;
  message: string;
  userMessage: string;
  recoverable: boolean;
  retryable: boolean;
}

export enum EntitlementErrorCode {
  NETWORK_ERROR = 'NETWORK_ERROR',
  AUTH_ERROR = 'AUTH_ERROR',
  DATABASE_ERROR = 'DATABASE_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  RATE_LIMIT_ERROR = 'RATE_LIMIT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  RLS_ERROR = 'RLS_ERROR',
  SUPABASE_ERROR = 'SUPABASE_ERROR'
}

/**
 * User-friendly error messages for entitlement failures
 */
const ENTITLEMENT_ERROR_MESSAGES: Record<EntitlementErrorCode, EntitlementError> = {
  [EntitlementErrorCode.NETWORK_ERROR]: {
    code: 'NETWORK_ERROR',
    message: 'Network connection failed',
    userMessage: 'Unable to check your subscription status due to a network issue. Please check your internet connection and try again.',
    recoverable: true,
    retryable: true
  },
  [EntitlementErrorCode.AUTH_ERROR]: {
    code: 'AUTH_ERROR',
    message: 'Authentication failed',
    userMessage: 'Unable to verify your account. Please sign out and sign back in to refresh your session.',
    recoverable: true,
    retryable: false
  },
  [EntitlementErrorCode.DATABASE_ERROR]: {
    code: 'DATABASE_ERROR',
    message: 'Database connection failed',
    userMessage: 'Our servers are temporarily unavailable. Please try again in a few moments.',
    recoverable: true,
    retryable: true
  },
  [EntitlementErrorCode.TIMEOUT_ERROR]: {
    code: 'TIMEOUT_ERROR',
    message: 'Request timed out',
    userMessage: 'The request is taking longer than expected. Please try again.',
    recoverable: true,
    retryable: true
  },
  [EntitlementErrorCode.RATE_LIMIT_ERROR]: {
    code: 'RATE_LIMIT_ERROR',
    message: 'Rate limit exceeded',
    userMessage: 'Too many requests. Please wait a moment before trying again.',
    recoverable: true,
    retryable: true
  },
  [EntitlementErrorCode.RLS_ERROR]: {
    code: 'RLS_ERROR',
    message: 'Row level security violation',
    userMessage: 'Unable to access your subscription data. Please sign out and sign back in.',
    recoverable: true,
    retryable: false
  },
  [EntitlementErrorCode.SUPABASE_ERROR]: {
    code: 'SUPABASE_ERROR',
    message: 'Supabase service error',
    userMessage: 'Our subscription service is temporarily unavailable. Please try again shortly.',
    recoverable: true,
    retryable: true
  },
  [EntitlementErrorCode.UNKNOWN_ERROR]: {
    code: 'UNKNOWN_ERROR',
    message: 'Unknown error occurred',
    userMessage: 'An unexpected error occurred while checking your subscription. Please refresh the page or try again later.',
    recoverable: true,
    retryable: true
  }
};

/**
 * Categorize errors from entitlement loading
 */
export function categorizeEntitlementError(error: any): EntitlementError {
  // Handle network errors
  if (error?.message?.includes('fetch') || error?.message?.includes('network') || error?.code === 'NETWORK_ERROR') {
    return ENTITLEMENT_ERROR_MESSAGES[EntitlementErrorCode.NETWORK_ERROR];
  }

  // Handle authentication errors
  if (error?.message?.includes('auth') || error?.message?.includes('unauthorized') || error?.code === 'PGRST301') {
    return ENTITLEMENT_ERROR_MESSAGES[EntitlementErrorCode.AUTH_ERROR];
  }

  // Handle RLS errors
  if (error?.message?.includes('RLS') || error?.message?.includes('row level security') || error?.code === 'PGRST116') {
    return ENTITLEMENT_ERROR_MESSAGES[EntitlementErrorCode.RLS_ERROR];
  }

  // Handle timeout errors
  if (error?.message?.includes('timeout') || error?.code === 'TIMEOUT') {
    return ENTITLEMENT_ERROR_MESSAGES[EntitlementErrorCode.TIMEOUT_ERROR];
  }

  // Handle rate limit errors
  if (error?.message?.includes('rate limit') || error?.code === '429') {
    return ENTITLEMENT_ERROR_MESSAGES[EntitlementErrorCode.RATE_LIMIT_ERROR];
  }

  // Handle Supabase-specific errors
  if (error?.message?.includes('supabase') || error?.message?.includes('postgrest')) {
    return ENTITLEMENT_ERROR_MESSAGES[EntitlementErrorCode.SUPABASE_ERROR];
  }

  // Handle database errors
  if (error?.message?.includes('database') || error?.message?.includes('connection') || error?.code?.startsWith('PG')) {
    return ENTITLEMENT_ERROR_MESSAGES[EntitlementErrorCode.DATABASE_ERROR];
  }

  // Default to unknown error
  return ENTITLEMENT_ERROR_MESSAGES[EntitlementErrorCode.UNKNOWN_ERROR];
}

/**
 * Get recovery suggestions for entitlement errors
 */
export function getRecoverySuggestions(error: EntitlementError): string[] {
  const suggestions: string[] = [];

  switch (error.code) {
    case EntitlementErrorCode.NETWORK_ERROR:
      suggestions.push('Check your internet connection');
      suggestions.push('Try refreshing the page');
      suggestions.push('Disable any VPN or proxy');
      break;

    case EntitlementErrorCode.AUTH_ERROR:
    case EntitlementErrorCode.RLS_ERROR:
      suggestions.push('Sign out and sign back in');
      suggestions.push('Clear your browser cache');
      suggestions.push('Try using an incognito/private window');
      break;

    case EntitlementErrorCode.DATABASE_ERROR:
    case EntitlementErrorCode.SUPABASE_ERROR:
      suggestions.push('Wait a few moments and try again');
      suggestions.push('Check our status page for service updates');
      suggestions.push('Contact support if the issue persists');
      break;

    case EntitlementErrorCode.TIMEOUT_ERROR:
      suggestions.push('Try again with a better internet connection');
      suggestions.push('Wait a moment before retrying');
      break;

    case EntitlementErrorCode.RATE_LIMIT_ERROR:
      suggestions.push('Wait 30 seconds before trying again');
      suggestions.push('Avoid refreshing the page repeatedly');
      break;

    default:
      suggestions.push('Refresh the page');
      suggestions.push('Try again in a few minutes');
      suggestions.push('Contact support if the problem continues');
  }

  return suggestions;
}

/**
 * Track entitlement errors for monitoring
 */
export function trackEntitlementError(error: EntitlementError, context?: any): void {
  // Track error for analytics (if available)
  if (typeof window !== 'undefined' && (window as any).analytics?.track) {
    (window as any).analytics.track('entitlement_error', {
      error_code: error.code,
      error_message: error.message,
      user_message: error.userMessage,
      recoverable: error.recoverable,
      retryable: error.retryable,
      context,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      url: window.location.href
    });
  }

  if (entitlementLogger.isEnabled()) {
    entitlementLogger.error('Entitlement error', {
      error,
      context,
      timestamp: new Date().toISOString()
    });
  }
}

/**
 * Create a standardized error response for entitlement failures
 */
export function createEntitlementErrorResponse(originalError: any, context?: any): {
  error: EntitlementError;
  suggestions: string[];
  timestamp: string;
} {
  const error = categorizeEntitlementError(originalError);
  const suggestions = getRecoverySuggestions(error);
  
  // Track the error
  trackEntitlementError(error, { originalError, context });

  return {
    error,
    suggestions,
    timestamp: new Date().toISOString()
  };
}

/**
 * Determine if an error should trigger a retry
 */
export function shouldRetryEntitlementError(error: EntitlementError): boolean {
  return error.retryable;
}

/**
 * Calculate retry delay with exponential backoff for entitlement errors
 */
export function calculateRetryDelay(attemptNumber: number, baseDelay: number = 1000): number {
  const maxDelay = 30000; // 30 seconds max
  const delay = baseDelay * Math.pow(2, attemptNumber - 1);
  return Math.min(delay, maxDelay);
}

const entitlementErrorHandler = {
  EntitlementErrorCode,
  categorizeEntitlementError,
  getRecoverySuggestions,
  trackEntitlementError,
  createEntitlementErrorResponse,
  shouldRetryEntitlementError,
  calculateRetryDelay
};

export default entitlementErrorHandler;
